import { useState } from "react";
import { fetchFeatures } from "../api/client";
import { ClaimAdminPanel } from "../components/ClaimAdminPanel";
import { Logo } from "../components/Logo";
import { NavTabs } from "../components/NavTabs";
import { useAsyncData } from "../hooks/useAsyncData";
import { useAuth } from "../hooks/useAuth";
import { useBranding } from "../hooks/useBranding";
import { useRunStatus } from "../hooks/useRunStatus";
import { AdminsScreen } from "./AdminsScreen";
import { AuditScreen } from "./AuditScreen";
import { ChatScreen } from "./ChatScreen";
import { RulesScreen } from "./RulesScreen";
import { RunsScreen } from "./RunsScreen";
import { ScopesScreen } from "./ScopesScreen";
import { StatusScreen } from "./StatusScreen";
import { defaultScreen, hiddenScreens } from "../navigation";
import type { ScreenId } from "../navigation";

/**
 * The signed-in shell: branding header, nav tabs, and the active screen.
 *
 * It serves two audiences from one frame. Anyone in the organisation gets the chat; administrators
 * additionally get the control-plane tabs. What each sees is decided by `hiddenScreens`, and
 * enforced — separately and authoritatively — by the server on every request.
 */
export function AppShell(): JSX.Element {
  const { branding } = useBranding();
  const { session, logout } = useAuth();
  const isAdmin = session?.is_admin ?? false;
  const chatAvailable = session?.chat_available ?? false;
  const [activeScreen, setActiveScreen] = useState<ScreenId>(defaultScreen(chatAvailable));

  // Lifted here, not owned by RunsScreen, so the run state (and the nav dot below) stays live no
  // matter which screen is open, and so switching screens doesn't tear down and restart the poll.
  // Administrators only: it polls an admin route, which anyone else would be refused.
  const runStatus = useRunStatus(isAdmin);
  const isRunning = runStatus.report?.status.state === "running";

  // Hidden until the flag is known, rather than shown and then withdrawn: a tab that appears for
  // one frame and vanishes reads as a bug. A failed fetch leaves it hidden for the same reason.
  const { data: features } = useAsyncData(
    () => (isAdmin ? fetchFeatures() : Promise.resolve(null)),
    [isAdmin],
  );
  const hidden = hiddenScreens({
    isAdmin,
    chatAvailable,
    scopeSelectionEnabled: features?.scope_selection_enabled ?? false,
  });

  return (
    <div className="admin-shell">
      <header className="admin-shell__header">
        <Logo branding={branding} />
        <NavTabs
          active={activeScreen}
          onChange={setActiveScreen}
          runningIndicatorFor={isRunning ? ["runs"] : []}
          hidden={hidden}
        />
        <div className="admin-shell__account">
          {/* The address when the IdP released one; the stable key otherwise (break-glass). */}
          <span className="admin-shell__user-key">{session?.email || session?.user_key}</span>
          <button type="button" className="button button--secondary" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>
      <main className="admin-shell__content">{renderScreen(activeScreen, runStatus)}</main>
      {!isAdmin && (
        <footer className="admin-shell__footer">
          <ClaimAdminPanel />
        </footer>
      )}
    </div>
  );
}

// A switch rather than the previous Record<ScreenId, Component> lookup: RunsScreen is the only
// screen that needs a prop (the lifted runStatus), and a uniform map can't express that without
// either widening every other screen's signature or casting. A switch keeps each screen's actual
// signature honest.
function renderScreen(screen: ScreenId, runStatus: ReturnType<typeof useRunStatus>): JSX.Element {
  switch (screen) {
    case "chat":
      return <ChatScreen />;
    case "status":
      return <StatusScreen />;
    case "scopes":
      return <ScopesScreen />;
    case "rules":
      return <RulesScreen />;
    case "admins":
      return <AdminsScreen />;
    case "audit":
      return <AuditScreen />;
    case "runs":
      return <RunsScreen runStatus={runStatus} />;
  }
}
